import React, { FC } from 'react';

import { useIntl } from 'react-intl';
import { useTheme } from 'styled-components';

import { BaseStyle, BaseStyleEnums } from '@nhl/nhl-breakaway-ui';

import CommonMessages from '~/core/commonMessages';
import { useSizeWithThrottle } from '~/core/hooks/useSizeWithThrottle';
import { defaultLocationData, useLocationQuery } from '~/redux/ducks/location';

import { DatePromo, PromoDetail } from '../../../../types';

import { Divider, PromoContainer, PromoHeader, PromoImage, Text, WatchContainer, WatchHeader } from './styles';

type Props = {
    promos?: Array<DatePromo>;
};

const Promo: FC<Props> = ({ promos }) => {
    const intl = useIntl();
    const screenSize = useSizeWithThrottle();
    const theme = useTheme();

    const isSmallerThanMedium = screenSize.width < BaseStyleEnums.ScreenWidthBreakpoints.medium;

    const { data: countryCode = defaultLocationData } = useLocationQuery({});

    let defaultPromo: DatePromo | undefined = undefined;
    const countryPromo =
        promos?.find((promo: DatePromo) => {
            // If no country is present then this will be the promo if nothing else is found
            if (!promo.country) {
                defaultPromo = promo;
            }
            return promo.country === countryCode;
        }) || defaultPromo;

    if (!countryPromo) {
        return null;
    }

    const locale = intl.locale || window.__SITE_SETTINGS__.languageCode;
    const promoItem = (countryPromo[locale] || countryPromo.default) as PromoDetail | undefined;

    const altText = intl.formatMessage(CommonMessages.misc.whereToWatch, { network: promoItem?.logoText });
    const logoUrl =
        theme.Surface.primary === BaseStyle.DarkTheme.Surface.primary
            ? promoItem?.darkLogoUrl
            : promoItem?.lightLogoUrl;

    let promoHeader = <PromoHeader>{promoItem?.text}</PromoHeader>;
    if (isSmallerThanMedium && promoItem?.text) {
        // TODO the backend will be updated so we have a better way of handling this
        const [info, dateTime] = promoItem.text.split(', ');
        promoHeader = (
            <PromoHeader>
                <Text>{info}</Text>
                <Text>{dateTime}</Text>
            </PromoHeader>
        );
    }

    const promoAndSecondaryTextExist = promoItem?.text && promoItem?.secondaryText;
    const promoOrSecondaryTextExist = !!(promoItem?.text || promoItem?.secondaryText);

    return promoItem ? (
        <PromoContainer $hasGap={!promoItem.secondaryText}>
            {promoHeader}
            {promoAndSecondaryTextExist && <Divider />}
            <WatchContainer $hasGap={promoOrSecondaryTextExist}>
                {promoItem.secondaryText && <WatchHeader>{promoItem.secondaryText}</WatchHeader>}

                {logoUrl &&
                    (promoItem.url ? (
                        <a href={promoItem.url} target="_blank" rel="noreferrer">
                            <PromoImage alt={altText} src={logoUrl} />
                        </a>
                    ) : (
                        <PromoImage alt={altText} src={logoUrl} />
                    ))}
            </WatchContainer>
        </PromoContainer>
    ) : null;
};

export default Promo;
